import { css } from "@emotion/react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useEffect, useRef, useState } from "react";
import useWebSocket from "react-use-websocket";
import ChatContactInfos from "../chat-infos/chat-contact-infos";
import useOmnichannel, {
  Message,
  PinnedMessage,
  compareMessagesByDateTimeAndId,
  iDialogMessagesFile,
  normalizeHistoryMessages
} from "../use-omnichannel";
import Input from "./input";
import Messages from "./messages";
import PinnedMessageBanner from "./pinned-message";
import Topbar from "./topbar";
import { TransferModal } from "./Transfer";
import {
  createThrottledProgressReporter,
  uploadFileChunked
} from "./chunked-upload";
import { AttachmentUploadProgress } from "./composer-attachments";
import { setLastMessageSent } from "../../redux/slices/sidebar";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { AuthContextType, useAuth } from "../../auth/auth-provider";
import { Dropdown } from "primereact/dropdown";
import BotsServices from "../../services/bots";
import { useToast } from "../../components/toast-provider";
import { setContactId } from "../../redux/slices/omnichannel";
import { ProgressSpinner } from "primereact/progressspinner";
import { setRefreshAllDialogs } from "../../redux/slices/omnichannel";
import { setAllDialogs } from "../../redux/slices/sidebar";
import { useNavigate } from "react-router";

const SLOW_CONNECTION_TOAST_DELAY_MS = 15_000;
const SLOW_CONNECTION_TOAST_MESSAGE =
  "A conexão com o servidor parece estar lenta. Por favor, aguarde um momento.";

export interface ChatProps {
  dialog: iDialogMessagesFile;
  onDialogFinalize: () => void;
  shouldScroll: boolean;
  onShouldScroll: (shouldScroll: boolean) => void;
  onDialogChange: (id: number) => Promise<void>;
  onDialogRefresh: () => Promise<void>;
  botSelected: number | null;
  onBotSelectedChange: (value: number | null) => void;
  startDialogFromContact: (payload: any) => Promise<any>;
}

export interface ChatMessageButton extends Record<string, unknown> {
  id: number;
  position: number;
  label: string;
  type: string;
  link: string;
}

export interface ChatMessageJson extends Record<string, unknown> {
  type?: string;
  id?: number;
  messageId?: number;
  dialogId?: number;
  sender?: string;
  humanName?: string;
  message?: string;
  dateTime?: string;
  dialogToken?: string;
  cannedMessageId?: number;
  clientTempId?: string;
  forwardMessageId?: number;
  forwardedMessage?: boolean;
  status?: string;
  errorMessage?: string;
  retryable?: boolean;
  file?: {
    id: number;
    mime: string;
    name: string;
    url: string;
  };
  files?: {
    id: number;
    mime: string;
    name: string;
    url: string;
  }[];
  buttons?: ChatMessageButton[];
  quotedMessage?: {
    parentId: number;
    message: string;
    mime?: string;
  };
  pinnedMessage?: PinnedMessage | null;
}

interface PinConfirmDialogProps {
  visible: boolean;
  message: string;
  onConfirm: () => void;
  onHide: () => void;
}

const PinConfirmDialog = ({
  visible,
  message,
  onConfirm,
  onHide
}: PinConfirmDialogProps) => (
  <Dialog
    visible={visible}
    onHide={onHide}
    draggable={false}
    header="Atenção"
    style={{ width: "575px" }}
  >
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div>{message}</div>
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          gap: "15px"
        }}
      >
        <Button
          label="Sim"
          className="p-button-danger"
          onClick={() => {
            onConfirm();
            onHide();
          }}
        />
        <Button label="Não" className="p-button-secondary" onClick={onHide} />
      </div>
    </div>
  </Dialog>
);

const toPinnedByDialogId = (pinnedMessages: PinnedMessage[] = []) =>
  pinnedMessages.reduce<Record<number, PinnedMessage>>((acc, pinned) => {
    acc[pinned.dialogId] = pinned;
    return acc;
  }, {});

const Chat = ({
  dialog,
  onDialogFinalize,
  shouldScroll,
  onDialogChange,
  onDialogRefresh,
  botSelected,
  onBotSelectedChange,
  startDialogFromContact
}: ChatProps) => {
  const { user, company } = useAuth() as AuthContextType;
  const navigate = useNavigate();
  const { lastJsonMessage, sendJsonMessage } = useWebSocket<ChatMessageJson>(
    process.env.NODE_ENV === "development"
      ? `ws://${process.env.OMNICHANNEL_URL}/omnichannel/${dialog.id}/${user.id}`
      : `wss://${process.env.OMNICHANNEL_URL}/omnichannel/${dialog.id}/${user.id}`,
    {
      retryOnError: true,
      shouldReconnect: () => true,
      reconnectAttempts: 10,
      reconnectInterval: 5_000,
      heartbeat: {
        interval: 10_000,
        message: `{ "ping": true }`,
        returnMessage: "pong",
        timeout: 60_000
      }
    }
  );

  const [infoSideBarState, setExpandedInfo] = useState(false);
  const [dialogInfo, setDialogInfo] = useState<iDialogMessagesFile>(dialog);
  const [messageHistory, setMessageHistory] = useState<Message[]>(
    normalizeHistoryMessages(dialog.messages, dialog)
  );
  const workspacesSelected = useAppSelector(
    (state) => state.workspaces.selectedWorkspaces
  );
  const allDialogs = useAppSelector((state) => state.sidebar.allDialogs);
  const orderedMessages = [...messageHistory].sort(
    compareMessagesByDateTimeAndId
  );

  const createClientTempId = () => {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID();
    }

    return `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const [screenMobile, setScreenMobile] = useState(false);

  const contactId = useAppSelector((state) => state.omnichannel.contactId);
  const [bots, setBots] = useState<
    {
      id: number;
      name: string;
      workspaceId: number;
      qrCodeConnected: boolean;
    }[]
  >([]);
  const toast = useToast();
  const [loadDialog, setLoadDialog] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] =
    useState<AttachmentUploadProgress | null>(null);
  const [contactInfoRefreshKey, setContactInfoRefreshKey] = useState(0);

  const [deletedParentIds, setDeletedParentIds] = useState<Set<number>>(
    () => new Set()
  );
  const [pinnedByDialogId, setPinnedByDialogId] = useState<
    Record<number, PinnedMessage>
  >(() => toPinnedByDialogId(dialog.pinnedMessages));
  const [messageToConfirmPin, setMessageToConfirmPin] =
    useState<Message | null>(null);
  const [confirmUnpinVisible, setConfirmUnpinVisible] = useState(false);
  const { pinMessage, unpinMessage } = useOmnichannel();
  const canManagePin =
    (user.admin ||
      (dialogInfo.transferred && dialogInfo.userTransferredId === user.id)) &&
    !dialogInfo.closedAt;

  const messageRef = useRef<HTMLDivElement>(null);
  const pendingMessageScrollRef = useRef(false);
  const pendingSlowConnectionTimeoutsRef = useRef<Map<string, number>>(
    new Map()
  );
  const visibleSlowConnectionToastIdsRef = useRef<Set<string>>(new Set());
  const slowConnectionToastVisibleRef = useRef(false);
  const lastSlowConnectionToastAtRef = useRef(0);
  const dispatch = useAppDispatch();
  const { GetBotsWhatsAppQrCode } = BotsServices();

  const scrollToBottom = () => {
    if (messageRef.current) {
      messageRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest"
      });
    }
  };

  const scheduleMessageScroll = () => {
    pendingMessageScrollRef.current = true;
  };

  const flushMessageScroll = () => {
    let attempts = 0;

    const run = () => {
      scrollToBottom();
      attempts += 1;

      if (attempts < 4) {
        window.requestAnimationFrame(run);
      }
    };

    window.requestAnimationFrame(run);
  };

  const onShouldSroll = (set: boolean) => {
    if (set) {
      setTimeout(() => {
        scrollToBottom();
      }, 0);
    }
  };

  const clearPendingSlowConnectionTimeout = (clientTempId?: string) => {
    if (!clientTempId) {
      return;
    }

    const timeoutId =
      pendingSlowConnectionTimeoutsRef.current.get(clientTempId);

    if (typeof timeoutId === "number") {
      window.clearTimeout(timeoutId);
    }

    pendingSlowConnectionTimeoutsRef.current.delete(clientTempId);

    if (visibleSlowConnectionToastIdsRef.current.delete(clientTempId)) {
      if (visibleSlowConnectionToastIdsRef.current.size === 0) {
        toast.current?.clear();
        slowConnectionToastVisibleRef.current = false;
      }
    }
  };

  const scheduleSlowConnectionToast = (clientTempId: string) => {
    clearPendingSlowConnectionTimeout(clientTempId);

    const timeoutId = window.setTimeout(() => {
      pendingSlowConnectionTimeoutsRef.current.delete(clientTempId);
      visibleSlowConnectionToastIdsRef.current.add(clientTempId);

      if (slowConnectionToastVisibleRef.current) {
        return;
      }

      const now = Date.now();

      if (now - lastSlowConnectionToastAtRef.current < 2_000) {
        return;
      }

      lastSlowConnectionToastAtRef.current = now;
      slowConnectionToastVisibleRef.current = true;
      toast.current?.show({
        sticky: true,
        severity: "warn",
        summary: "Aviso",
        detail: SLOW_CONNECTION_TOAST_MESSAGE
      });
    }, SLOW_CONNECTION_TOAST_DELAY_MS);

    pendingSlowConnectionTimeoutsRef.current.set(clientTempId, timeoutId);
  };

  useEffect(() => {
    dispatch(setContactId(0));
    pendingSlowConnectionTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    pendingSlowConnectionTimeoutsRef.current.clear();
    visibleSlowConnectionToastIdsRef.current.clear();
    if (slowConnectionToastVisibleRef.current) {
      toast.current?.clear();
      slowConnectionToastVisibleRef.current = false;
    }
    setDialogInfo(dialog);
    setDeletedParentIds(new Set());
    setPinnedByDialogId(toPinnedByDialogId(dialog?.pinnedMessages));
    setMessageToConfirmPin(null);
    setConfirmUnpinVisible(false);
    setMessageHistory(normalizeHistoryMessages(dialog?.messages ?? [], dialog));
  }, [dialog, dispatch, toast]);

  useEffect(() => {
    if (contactId !== 0) {
      setExpandedInfo(false);
    }
  }, [contactId]);

  useEffect(
    () => () => {
      pendingSlowConnectionTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      pendingSlowConnectionTimeoutsRef.current.clear();
      visibleSlowConnectionToastIdsRef.current.clear();
      if (slowConnectionToastVisibleRef.current) {
        toast.current?.clear();
        slowConnectionToastVisibleRef.current = false;
      }
    },
    [toast]
  );

  const GetChatbots = async () => {
    const companyId = user.admin ? company?.id : user.companyId;
    const availableWorkspaces =
      workspacesSelected?.length > 0
        ? workspacesSelected
        : user?.workspaces?.workspaces?.map((workspace) => workspace.id) ?? [];

    if (!companyId || !availableWorkspaces.length) {
      return;
    }

    try {
      const results = await GetBotsWhatsAppQrCode({
        companyId,
        workspaces: availableWorkspaces
      });
      setBots(results);
    } catch (error) {
      toast?.current?.show({
        life: 3000,
        severity: "error",
        summary: "Erro.",
        detail: "Erro ao buscar chatbots. Por favor tente mais tarde."
      });
    }
  };

  useEffect(() => {
    GetChatbots();
  }, [
    user.admin,
    user.companyId,
    company?.id,
    workspacesSelected,
    user?.workspaces?.workspaces
  ]);

  useEffect(() => {
    if (lastJsonMessage !== null) {
      if (lastJsonMessage?.type === "dialog_finalized") {
        const remaining = allDialogs.filter((id) => id !== dialog.id);

        dispatch(setAllDialogs(remaining));
        dispatch(setRefreshAllDialogs(true));

        if (remaining.length === 0) {
          navigate({ pathname: "/rooms/" });
        }

        onDialogFinalize();
        return;
      }

      if (lastJsonMessage?.type === "send_error") {
        const failedTempId = lastJsonMessage.clientTempId;
        const errorMessage =
          lastJsonMessage.errorMessage ??
          "Não é possível enviar mensagens neste momento. Por favor, tente mais tarde";
        const retryable = lastJsonMessage.retryable !== false;

        clearPendingSlowConnectionTimeout(failedTempId);

        if (retryable) {
          scheduleMessageScroll();
          setMessageHistory((prev) =>
            prev.map((msg) =>
              msg.clientTempId === failedTempId
                ? {
                    ...msg,
                    sendState: "failed",
                    status: "ERROR",
                    retryable: true,
                    errorText: errorMessage
                  }
                : msg
            )
          );
        } else {
          setMessageHistory((prev) =>
            prev.filter((msg) => msg.clientTempId !== failedTempId)
          );
        }
        toast.current?.show({
          life: 5000,
          severity: "warn",
          summary: "Aviso",
          detail: errorMessage
        });
        return;
      }

      if (lastJsonMessage?.type === "message_status_update") {
        const updatedMessageId = lastJsonMessage.messageId;
        const updatedStatus = lastJsonMessage.status;

        if (typeof updatedMessageId === "number" && updatedStatus) {
          setMessageHistory((prev) =>
            prev.map((msg) =>
              msg.id === updatedMessageId
                ? {
                    ...msg,
                    status: updatedStatus
                  }
                : msg
            )
          );
        }

        return;
      }

      if (lastJsonMessage?.type === "pinned_message_updated") {
        if (typeof lastJsonMessage.dialogId === "number") {
          setPinnedForDialog(
            lastJsonMessage.dialogId,
            lastJsonMessage.pinnedMessage ?? null
          );
        }

        return;
      }

      const lastJsonMessageTyped = {
        ...(lastJsonMessage as unknown as Message),
        sourceDialogId: dialog.id,
        sourceDialogDateTime: dialog.dateTime,
        sourceDialogName: dialog.name,
        sourceBotName: dialog.botName,
        sourceUserName: lastJsonMessage.humanName ?? user.name
      };

      if (lastJsonMessage?.file?.name?.length) {
        lastJsonMessageTyped.files = [
          {
            id: String(lastJsonMessage?.file.id),
            mime: lastJsonMessage?.file.mime,
            fileName: lastJsonMessage?.file.name,
            url: lastJsonMessage?.file.url
          }
        ];
      } else if (lastJsonMessage?.files?.length) {
        lastJsonMessageTyped.files = lastJsonMessage?.files.map((el) => ({
          id: String(el.id),
          mime: el.mime,
          fileName: el.name,
          url: el.url
        }));
      }

      const quotedParentId = (lastJsonMessage as any)?.quotedMessage?.parentId;
      if (typeof quotedParentId === "number" && quotedParentId > 0) {
        lastJsonMessageTyped.parentId = quotedParentId;
      }

      if (lastJsonMessageTyped?.message === "Mensagem apagada") {
        markMessageDeleted(lastJsonMessageTyped.id);
      }

      clearPendingSlowConnectionTimeout(lastJsonMessage.clientTempId);
      scheduleMessageScroll();
      setMessageHistory((prev) => {
        const clientTempId = lastJsonMessage.clientTempId;
        const withoutTemp = clientTempId
          ? prev.filter((msg) => msg.clientTempId !== clientTempId)
          : prev;
        const messageId = prev.findIndex(
          (msg) => msg.id === lastJsonMessageTyped.id
        );

        if (
          messageId !== -1 &&
          !lastJsonMessageTyped.updatedAt &&
          lastJsonMessageTyped.message !== "Mensagem apagada"
        ) {
          lastJsonMessageTyped.updatedAt = new Date().toISOString();
        }

        if (messageId !== -1) {
          const updatedMessages = [...withoutTemp];
          const updatedIndex = updatedMessages.findIndex(
            (msg) => msg.id === lastJsonMessageTyped.id
          );
          if (updatedIndex === -1) {
            return [...updatedMessages, lastJsonMessageTyped];
          }
          updatedMessages[updatedIndex] = lastJsonMessageTyped;
          return updatedMessages;
        }

        return [...withoutTemp, lastJsonMessageTyped];
      });
    }
  }, [shouldScroll, lastJsonMessage]);

  useEffect(() => {
    if (!pendingMessageScrollRef.current) {
      return;
    }

    pendingMessageScrollRef.current = false;
    flushMessageScroll();
  }, [messageHistory]);

  const sendWithTemporaryState = (payload: {
    message: string;
    cannedMessageId?: number;
    fileId?: number;
    files?: Message["files"];
    quotedMessageId?: number;
    forwardMessageId?: number;
    quotedMessage?: {
      parentId: number;
      message: string;
      mime?: string;
    };
  }) => {
    const clientTempId = createClientTempId();
    const now = new Date().toISOString();

    scheduleMessageScroll();
    setMessageHistory((prev) => [
      ...prev,
      {
        id: Date.now(),
        message: payload.message,
        sender: "HUMAN",
        dateTime: now,
        file: "",
        buttons: [],
        files: payload.files ?? [],
        sourceDialogId: dialog.id,
        sourceDialogDateTime: dialog.dateTime,
        sourceDialogName: dialog.name,
        sourceBotName: dialog.botName,
        sourceUserName: user.name,
        parentId: payload.quotedMessageId ?? 0,
        quotedMessage: payload.quotedMessage,
        forwardedMessage: Boolean(payload.forwardMessageId),
        clientTempId,
        status: "PENDING",
        sendState: "sending",
        retryable: false,
        retryPayload: {
          message: payload.message,
          cannedMessageId: payload.cannedMessageId,
          fileId: payload.fileId,
          files: payload.files,
          quotedMessageId: payload.quotedMessageId,
          forwardMessageId: payload.forwardMessageId,
          quotedMessage: payload.quotedMessage
        }
      }
    ]);

    scheduleSlowConnectionToast(clientTempId);
    sendJsonMessage(
      payload.forwardMessageId
        ? {
            forwardMessageId: payload.forwardMessageId,
            clientTempId
          }
        : {
            ...payload,
            clientTempId
          }
    );
    dispatch(setLastMessageSent(payload.message));
  };

  const retryTemporaryMessage = (clientTempId: string) => {
    const failedMessage = messageHistory.find(
      (msg) => msg.clientTempId === clientTempId
    );

    if (!failedMessage?.retryPayload) {
      return;
    }

    scheduleSlowConnectionToast(clientTempId);
    scheduleMessageScroll();
    setMessageHistory((prev) =>
      prev.map((msg) =>
        msg.clientTempId === clientTempId
          ? {
              ...msg,
              sendState: "sending",
              status: "PENDING",
              retryable: false,
              errorText: undefined
            }
          : msg
      )
    );

    sendJsonMessage(
      failedMessage.retryPayload.forwardMessageId
        ? {
            forwardMessageId: failedMessage.retryPayload.forwardMessageId,
            clientTempId
          }
        : {
            ...failedMessage.retryPayload,
            clientTempId
          }
    );
  };

  const setPinnedForDialog = (
    dialogId: number,
    pinned: PinnedMessage | null
  ) => {
    setPinnedByDialogId((prev) => {
      const next = { ...prev };
      if (pinned) {
        next[dialogId] = pinned;
      } else {
        delete next[dialogId];
      }
      return next;
    });
  };

  const handlePinMessage = async (messageToPin: Message) => {
    try {
      await pinMessage(dialogInfo.id, messageToPin.id);

      const firstFile = messageToPin.files?.[0];
      const hasText = Boolean(messageToPin.message?.trim());

      setPinnedForDialog(dialogInfo.id, {
        dialogId: dialogInfo.id,
        messageId: messageToPin.id,
        message: hasText ? messageToPin.message : firstFile?.url ?? "",
        mime: hasText ? undefined : firstFile?.mime,
        sender: messageToPin.sender,
        dateTime: messageToPin.dateTime
      });

      toast.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso",
        detail: "Mensagem fixada com sucesso!"
      });
    } catch (error: any) {
      toast.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: error?.message || "Não foi possível fixar a mensagem."
      });
    }
  };

  const currentPinnedMessage = pinnedByDialogId[dialogInfo.id];

  const requestPinMessage = (messageToPin: Message) => {
    if (!pinnedByDialogId[dialogInfo.id]) {
      handlePinMessage(messageToPin);
      return;
    }

    setMessageToConfirmPin(messageToPin);
  };

  const handleUnpinMessage = async () => {
    try {
      await unpinMessage(dialogInfo.id);

      setPinnedForDialog(dialogInfo.id, null);

      toast.current?.show({
        life: 3000,
        severity: "success",
        summary: "Sucesso",
        detail: "Mensagem desafixada com sucesso!"
      });
    } catch (error: any) {
      toast.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: error?.message || "Não foi possível desafixar a mensagem."
      });
    }
  };

  const markMessageDeleted = (id: number) => {
    setDeletedParentIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  useEffect(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 100);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 500) {
        setScreenMobile(true);
      } else {
        setScreenMobile(false);
      }
    };

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div
      css={css`
        display: flex;
        width: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          position: relative;
        `}
      >
        {loadDialog && (
          <div
            css={css`
              position: absolute;
              inset: 0;
              z-index: 9999;
              display: flex;
              align-items: center;
              justify-content: center;
              background: rgba(255, 255, 255, 0.7);
              pointer-events: all;
            `}
          >
            <div
              css={css`
                transform: scale(2.2);
              `}
            >
              <ProgressSpinner />
            </div>
          </div>
        )}
        <div
          css={css`
            display: flex;
            flex-direction: column;
            flex: 1;
            overflow: auto;
          `}
        >
          <Topbar
            dialog={dialog}
            onFinalize={onDialogFinalize}
            onOpenContactInfos={() => setExpandedInfo(true)}
          />
          {currentPinnedMessage && (
            <PinnedMessageBanner
              pinnedMessage={currentPinnedMessage}
              canRemove={canManagePin}
              onRemove={() => setConfirmUnpinVisible(true)}
              onClick={() => {
                document
                  .querySelector(
                    `[data-message-id="${currentPinnedMessage.messageId}"]`
                  )
                  ?.scrollIntoView({ behavior: "smooth", block: "center" });
              }}
            />
          )}
          <TransferModal />
          <PinConfirmDialog
            visible={messageToConfirmPin !== null}
            message="Já existe uma mensagem fixada nesta conversa. Ao continuar, ela será removida e substituída por esta. Deseja continuar?"
            onConfirm={() => {
              if (messageToConfirmPin) {
                handlePinMessage(messageToConfirmPin);
              }
            }}
            onHide={() => setMessageToConfirmPin(null)}
          />
          <PinConfirmDialog
            visible={confirmUnpinVisible}
            message="Ao continuar, a mensagem fixada será removida desta conversa. Deseja continuar?"
            onConfirm={handleUnpinMessage}
            onHide={() => setConfirmUnpinVisible(false)}
          />
          <Messages
            dialogInfo={dialogInfo}
            dialog={orderedMessages}
            shouldScroll={shouldScroll}
            onShouldScroll={() => onShouldSroll(false)}
            messageRef={messageRef}
            deletedParentIds={deletedParentIds}
            onMessageDeleted={markMessageDeleted}
            onRetryMessage={retryTemporaryMessage}
            pinnedMessageId={pinnedByDialogId[dialogInfo.id]?.messageId}
            canManagePin={canManagePin}
            onPinMessage={requestPinMessage}
            onUnpinMessage={handleUnpinMessage}
            onForwardMessage={async (
              messageId,
              text,
              contactIds,
              sourceDialogId
            ) => {
              if (!dialog.botId) {
                toast.current?.show({
                  life: 5000,
                  severity: "error",
                  summary: "Erro.",
                  detail: "Não foi possível identificar o bot da conversa."
                });
                return;
              }

              await Promise.all(
                contactIds.map((contactId) =>
                  startDialogFromContact({
                    userId: user.id,
                    botId: dialog.botId,
                    contactId,
                    message: text,
                    forwardMessageId: messageId,
                    forwardSourceDialogId: sourceDialogId
                  })
                )
              );
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "center",
            paddingLeft: "20px",
            background: contactId > 0 ? "white" : "transparent",
            paddingTop: contactId > 0 ? "20px" : "0px",
            zIndex: contactId > 0 ? 10 : 0
          }}
        >
          {contactId !== 0 && (
            <>
              <Dropdown
                style={{
                  width: "200px",
                  height: "60px",
                  lineHeight: "1.3125rem",
                  borderRadius: "24px",
                  border: "2px solid #cbced1"
                }}
                value={botSelected}
                options={bots}
                onChange={(e) => onBotSelectedChange(e.value)}
                optionLabel="name"
                optionValue="id"
                placeholder="Selecione um robô"
                filter={true}
                emptyMessage="Sem robôs disponíveis"
              />
            </>
          )}
          <Input
            dialog={dialog}
            botSelected={botSelected}
            fileUploadProgress={fileUploadProgress}
            onSendMessage={(payload) => {
              if (dialog.whatsappQrCode) {
                sendWithTemporaryState(payload);
                return;
              }

              sendJsonMessage(payload);
              dispatch(setLastMessageSent(payload.message));
              onShouldSroll(true);
            }}
            onFilesAttached={async (files, messageText = "") => {
              const text = messageText.trim();

              for (let index = 0; index < files.length; index += 1) {
                const file = files[index];
                const fileMessage = index === 0 ? text : "";
                const reportProgress = createThrottledProgressReporter(
                  (fraction) => {
                    setFileUploadProgress(
                      fraction >= 1 ? null : { index, progress: fraction }
                    );
                  }
                );

                let uploaded;
                try {
                  // eslint-disable-next-line no-await-in-loop
                  uploaded = await uploadFileChunked(file, reportProgress);
                } catch (error: any) {
                  setFileUploadProgress(null);
                  toast.current?.show({
                    life: 5000,
                    severity: "error",
                    summary: "Erro.",
                    detail:
                      error?.message ??
                      `Não foi possível enviar o arquivo ${file.name}.`
                  });
                  continue;
                }

                setFileUploadProgress(null);

                if (dialog.whatsappQrCode) {
                  sendWithTemporaryState({
                    message: fileMessage,
                    fileId: uploaded.id,
                    files: [
                      {
                        id: String(uploaded.id),
                        mime: uploaded.mime,
                        fileName: uploaded.name,
                        url: uploaded.url
                      }
                    ]
                  });
                  continue;
                }

                sendJsonMessage({
                  message: fileMessage,
                  fileId: uploaded.id
                });
              }

              onShouldSroll(true);
            }}
            onDialogChange={onDialogChange}
            onDialogRefresh={async () => {
              await onDialogRefresh();
              setContactInfoRefreshKey((value) => value + 1);
            }}
            startDialogFromContact={startDialogFromContact}
          />
        </div>
      </div>

      {!screenMobile && contactId === 0 && (
        <div
          css={css`
            display: flex;
            align-items: center;
            right: 0;
            z-index: 1;
            position: absolute;
          `}
        >
          <Button
            className="p-button-secondary openCloseButtonChatInfos"
            onClick={() => {
              setExpandedInfo((previous) => !previous);
            }}
            label={infoSideBarState ? ">" : "<"}
            css={
              infoSideBarState
                ? css`
                    position: static !important;
                    margin-bottom: 14vh;
                  `
                : css`
                    position: fixed !important;
                  `
            }
          />
          <div
            css={css`
              display: flex;
              right: 0;
            `}
          >
            {infoSideBarState ? (
              <ChatContactInfos
                dialog={dialog}
                onContactUpdated={onDialogRefresh}
                refreshKey={contactInfoRefreshKey}
              />
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default Chat;
